import React from 'react'
import { useChartSettings } from '../../hooks/useChartSettings'
import { ChartLibrary, CHART_LIBRARY_COMPARISON } from '../../types/chartSettings'

export function ChartSettings() {
  const { chartSettings, setChartLibrary, updateCanvasJSSettings, updateChartJSSettings, resetChartSettings } =
    useChartSettings()

  const handleLibraryChange = (library: ChartLibrary) => {
    setChartLibrary(library)
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Chart Library Settings</h3>
        <p className="text-sm text-gray-600">
          Choose between CanvasJS (current) and Chart.js (new open-source option)
        </p>
      </div>

      {/* Library Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* CanvasJS Option */}
        <div
          className={`p-5 border-2 rounded-lg cursor-pointer transition-all ${
            chartSettings.library === 'canvasjs'
              ? 'border-blue-500 bg-blue-50 shadow-md'
              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
          }`}
          onClick={() => handleLibraryChange('canvasjs')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <input
                type="radio"
                name="chartLibrary"
                value="canvasjs"
                checked={chartSettings.library === 'canvasjs'}
                onChange={() => handleLibraryChange('canvasjs')}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-2 font-semibold text-gray-900">CanvasJS (Classic)</span>
            </div>
            <span className="px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">Current</span>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center text-green-700">
              <span className="mr-2">✓</span>
              <span>Professional styling</span>
            </div>
            <div className="flex items-center text-green-700">
              <span className="mr-2">✓</span>
              <span>Built-in export (PNG/PDF/SVG)</span>
            </div>
            <div className="flex items-center text-green-700">
              <span className="mr-2">✓</span>
              <span>Advanced interactions</span>
            </div>
            <div className="flex items-center text-red-600">
              <span className="mr-2">✗</span>
              <span>Commercial license required</span>
            </div>
            <div className="flex items-center text-red-600">
              <span className="mr-2">✗</span>
              <span>Larger bundle (~500KB)</span>
            </div>
          </div>
        </div>

        {/* Chart.js Option */}
        <div
          className={`p-5 border-2 rounded-lg cursor-pointer transition-all ${
            chartSettings.library === 'chartjs'
              ? 'border-blue-500 bg-blue-50 shadow-md'
              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
          }`}
          onClick={() => handleLibraryChange('chartjs')}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <input
                type="radio"
                name="chartLibrary"
                value="chartjs"
                checked={chartSettings.library === 'chartjs'}
                onChange={() => handleLibraryChange('chartjs')}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-2 font-semibold text-gray-900">Chart.js (Modern)</span>
            </div>
            <span className="px-2.5 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">New</span>
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center text-green-700">
              <span className="mr-2">✓</span>
              <span>Open source (MIT)</span>
            </div>
            <div className="flex items-center text-green-700">
              <span className="mr-2">✓</span>
              <span>Smaller bundle (~200KB)</span>
            </div>
            <div className="flex items-center text-green-700">
              <span className="mr-2">✓</span>
              <span>Better performance</span>
            </div>
            <div className="flex items-center text-green-700">
              <span className="mr-2">✓</span>
              <span>Active community</span>
            </div>
            <div className="flex items-center text-yellow-600">
              <span className="mr-2">⚠</span>
              <span>Custom export implementation</span>
            </div>
          </div>
        </div>
      </div>

      {/* CanvasJS Settings */}
      {chartSettings.library === 'canvasjs' && (
        <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">CanvasJS Options</h4>

          <div className="space-y-4">
            {/* Theme Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
              <select
                value={chartSettings.canvasjs.theme}
                onChange={(e) =>
                  updateCanvasJSSettings({
                    theme: e.target.value as any,
                  })
                }
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="light1">Light 1</option>
                <option value="light2">Light 2</option>
                <option value="dark1">Dark 1</option>
                <option value="dark2">Dark 2</option>
              </select>
            </div>

            {/* Animation Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="canvasjs-animation"
                checked={chartSettings.canvasjs.animationEnabled}
                onChange={(e) =>
                  updateCanvasJSSettings({
                    animationEnabled: e.target.checked,
                  })
                }
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="canvasjs-animation" className="ml-2 text-sm font-medium text-gray-700">
                Enable animations
              </label>
            </div>

            {/* Export Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="canvasjs-export"
                checked={chartSettings.canvasjs.exportEnabled}
                onChange={(e) =>
                  updateCanvasJSSettings({
                    exportEnabled: e.target.checked,
                  })
                }
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="canvasjs-export" className="ml-2 text-sm font-medium text-gray-700">
                Enable export options
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Chart.js Settings */}
      {chartSettings.library === 'chartjs' && (
        <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">Chart.js Options</h4>

          <div className="space-y-4">
            {/* Animation Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="chartjs-animation"
                checked={chartSettings.chartjs.animation}
                onChange={(e) =>
                  updateChartJSSettings({
                    animation: e.target.checked,
                  })
                }
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="chartjs-animation" className="ml-2 text-sm font-medium text-gray-700">
                Enable animations
              </label>
            </div>

            {/* Legend Display Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="chartjs-legend"
                checked={chartSettings.chartjs.plugins.legend.display}
                onChange={(e) =>
                  updateChartJSSettings({
                    plugins: {
                      ...chartSettings.chartjs.plugins,
                      legend: {
                        ...chartSettings.chartjs.plugins.legend,
                        display: e.target.checked,
                      },
                    },
                  })
                }
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="chartjs-legend" className="ml-2 text-sm font-medium text-gray-700">
                Show legend
              </label>
            </div>

            {/* Legend Position Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Legend Position</label>
              <select
                value={chartSettings.chartjs.plugins.legend.position}
                onChange={(e) =>
                  updateChartJSSettings({
                    plugins: {
                      ...chartSettings.chartjs.plugins,
                      legend: {
                        ...chartSettings.chartjs.plugins.legend,
                        position: e.target.value as any,
                      },
                    },
                  })
                }
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
            </div>

            {/* Zoom Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="chartjs-zoom"
                checked={chartSettings.chartjs.plugins.zoom.enabled}
                onChange={(e) =>
                  updateChartJSSettings({
                    plugins: {
                      ...chartSettings.chartjs.plugins,
                      zoom: {
                        ...chartSettings.chartjs.plugins.zoom,
                        enabled: e.target.checked,
                      },
                    },
                  })
                }
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="chartjs-zoom" className="ml-2 text-sm font-medium text-gray-700">
                Enable zoom and pan
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Reset Button */}
      <div className="pt-4 border-t border-gray-200">
        <button
          onClick={resetChartSettings}
          className="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
        >
          Reset to Defaults
        </button>
      </div>

      {/* Feature Comparison Table */}
      <div className="pt-6">
        <h4 className="font-semibold text-gray-900 mb-4">Feature Comparison</h4>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Feature
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  CanvasJS
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Chart.js
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(CHART_LIBRARY_COMPARISON.canvasjs).map(([feature, canvasValue]) => (
                <tr key={feature} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {feature === 'bundleSize'
                      ? 'Bundle Size'
                      : feature === 'licensing'
                      ? 'Licensing'
                      : feature.replace(/([A-Z])/g, ' $1').trim().replace(/^./, (str) => str.toUpperCase())}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    {typeof canvasValue === 'boolean' ? (
                      canvasValue ? (
                        <span className="text-green-600 text-lg">✓</span>
                      ) : (
                        <span className="text-red-600 text-lg">✗</span>
                      )
                    ) : (
                      <span className="text-gray-700">{canvasValue}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    {typeof CHART_LIBRARY_COMPARISON.chartjs[
                      feature as keyof typeof CHART_LIBRARY_COMPARISON.chartjs
                    ] === 'boolean' ? (
                      CHART_LIBRARY_COMPARISON.chartjs[feature as keyof typeof CHART_LIBRARY_COMPARISON.chartjs] ? (
                        <span className="text-green-600 text-lg">✓</span>
                      ) : (
                        <span className="text-red-600 text-lg">✗</span>
                      )
                    ) : (
                      <span className="text-gray-700">
                        {CHART_LIBRARY_COMPARISON.chartjs[feature as keyof typeof CHART_LIBRARY_COMPARISON.chartjs]}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
